class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.hoverQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  }

  connectedCallback() {
    this.header = this.closest('.header-wrapper');
    this.navigation = this.closest('.header__inline-menu');
    this.events?.abort();
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.addEventListener('pointerenter', this.onPointerEnter.bind(this), options);
    this.addEventListener('pointerleave', this.onPointerLeave.bind(this), options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.peers().forEach((menu) => menu.cancelTimers());
    }, options);
    this.summary.addEventListener('click', () => this.cancelTimers(), options);
    this.hoverQuery.addEventListener('change', () => this.close(), options);
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = new ResizeObserver(() => {
      if (!this.canHover()) this.close();
    });
    if (this.navigation) this.visibilityObserver.observe(this.navigation);
    this.summary.setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
  }

  disconnectedCallback() {
    this.close();
    this.events?.abort();
    this.visibilityObserver?.disconnect();
  }

  peers() {
    return this.navigation ? [...this.navigation.querySelectorAll('header-menu')] : [this];
  }

  canHover() {
    return this.hoverQuery.matches && Boolean(this.navigation?.getClientRects().length);
  }

  cancelTimers() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.focusTimer);
  }

  onPointerEnter(event) {
    if (event.pointerType !== 'mouse' || !this.canHover()) return;
    this.cancelTimers();
    if (this.mainDetailsToggle.open) return;
    if (this.peers().some((menu) => menu !== this && menu.mainDetailsToggle.open)) {
      this.openFromPointer();
    } else {
      this.openTimer = window.setTimeout(() => this.openFromPointer(), 100);
    }
  }

  openFromPointer() {
    if (!this.canHover() || !this.matches(':hover')) return;
    if (this.peers().some((menu) => menu !== this && menu.content.contains(document.activeElement))) return;
    this.cancelTimers();
    this.peers().forEach((menu) => {
      if (menu !== this) menu.close();
    });
    this.mainDetailsToggle.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    this.syncHeaderState();
  }

  onPointerLeave(event) {
    if (event.pointerType !== 'mouse') return;
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      if (!this.matches(':hover') && !this.contains(document.activeElement)) this.close();
    }, 220);
  }

  onFocusOut() {
    window.clearTimeout(this.focusTimer);
    this.focusTimer = window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  syncHeaderState() {
    if (this.header) this.header.preventHide = Boolean(this.header.querySelector('header-menu > details[open]'));
  }

  onToggle() {
    this.summary.setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
    if (this.mainDetailsToggle.open) {
      this.peers().forEach((menu) => {
        if (menu !== this) menu.close();
      });
    }
    this.syncHeaderState();
    if (!this.header || document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  close() {
    this.cancelTimers();
    super.close();
    this.syncHeaderState();
  }
}

customElements.define('header-menu', HeaderMenu);
